import React, { Fragment } from "react";
import { parseRow } from "../../../../utils/diffUtils";

/**
 * DiffView component - renders a visual diff between two states
 * Used for displaying changes in rulesets, features, and families
 */
const DiffView = ({ diff }) => {
    if (!diff) return null;

    const lines = [];
    let addedRows = [];
    let deletedRows = [];
    let hasTableDiff = false;
    let columns = [];

    if (diff.type === 'RuleSet' && diff.details) {
        if (diff.action === 'Added') {
            lines.push({ type: 'add', text: `+ Entity: ${diff.name} (${diff.type})` });
            const addedRowsIdx = diff.details.indexOf('Added rows: [');
            if (addedRowsIdx !== -1) {
                const headerText = diff.details.substring(0, addedRowsIdx).trim();
                lines.push({ type: 'add', text: `+ Details: ${headerText}` });
                const rowsStr = diff.details.substring(addedRowsIdx + 'Added rows: ['.length, diff.details.length - 1);
                if (rowsStr) {
                    addedRows = rowsStr.split('; ').map(parseRow);
                }
            } else {
                lines.push({ type: 'add', text: `+ Details: ${diff.details}` });
            }
        } else if (diff.action === 'Deleted') {
            lines.push({ type: 'delete', text: `- Entity: ${diff.name} (${diff.type})` });
            const deletedRowsIdx = diff.details.indexOf('Deleted rows: [');
            if (deletedRowsIdx !== -1) {
                const headerText = diff.details.substring(0, deletedRowsIdx).trim();
                lines.push({ type: 'delete', text: `- Details: ${headerText}` });
                const rowsStr = diff.details.substring(deletedRowsIdx + 'Deleted rows: ['.length, diff.details.length - 1);
                if (rowsStr) {
                    deletedRows = rowsStr.split('; ').map(parseRow);
                }
            } else {
                lines.push({ type: 'delete', text: `- Details: ${diff.details}` });
            }
        } else if (diff.action === 'Modified') {
            lines.push({ type: 'neutral', text: `Entity: ${diff.name} (${diff.type})` });
            const detailParts = diff.details.split('; ');
            detailParts.forEach(part => {
                if (part.startsWith('Renamed from')) {
                    const match = part.match(/Renamed from '(.*)'/);
                    const oldName = match ? match[1] : '';
                    lines.push({ type: 'delete', text: `- Name: ${oldName}` });
                    lines.push({ type: 'add', text: `+ Name: ${diff.name}` });
                } else if (part.startsWith('Added rows:')) {
                    const rowsStr = part.substring('Added rows: ['.length, part.length - 1);
                    if (rowsStr) {
                        addedRows = rowsStr.split('; ').map(parseRow);
                    }
                } else if (part.startsWith('Deleted rows:')) {
                    const rowsStr = part.substring('Deleted rows: ['.length, part.length - 1);
                    if (rowsStr) {
                        deletedRows = rowsStr.split('; ').map(parseRow);
                    }
                } else if (part.startsWith('Row count changed from')) {
                    const match = part.match(/Row count changed from (\d+) to (\d+)/);
                    if (match) {
                        lines.push({ type: 'delete', text: `- Rows: ${match[1]}` });
                        lines.push({ type: 'add', text: `+ Rows: ${match[2]}` });
                    } else {
                        lines.push({ type: 'neutral', text: part });
                    }
                } else {
                    lines.push({ type: 'neutral', text: part });
                }
            });
        }

        if (addedRows.length > 0 || deletedRows.length > 0) {
            hasTableDiff = true;
            const columnsSet = new Set();
            addedRows.forEach(row => Object.keys(row).forEach(col => columnsSet.add(col)));
            deletedRows.forEach(row => Object.keys(row).forEach(col => columnsSet.add(col)));
            columns = Array.from(columnsSet).sort();
        }
    } else {
        if (diff.action === 'Added') {
            lines.push({ type: 'add', text: `+ Entity: ${diff.name} (${diff.type})` });
            if (diff.details) {
                lines.push({ type: 'add', text: `+ Details: ${diff.details}` });
            }
        } else if (diff.action === 'Deleted') {
            lines.push({ type: 'delete', text: `- Entity: ${diff.name} (${diff.type})` });
            if (diff.details) {
                lines.push({ type: 'delete', text: `- Details: ${diff.details}` });
            }
        } else if (diff.action === 'Modified') {
            lines.push({ type: 'neutral', text: `Entity: ${diff.name} (${diff.type})` });
            if (diff.details) {
                const detailParts = diff.details.split('; ');
                detailParts.forEach(part => {
                    if (part.startsWith('Renamed from')) {
                        const match = part.match(/Renamed from '(.*)'/);
                        const oldName = match ? match[1] : '';
                        lines.push({ type: 'delete', text: `- Name: ${oldName}` });
                        lines.push({ type: 'add', text: `+ Name: ${diff.name}` });
                    } else if (part.startsWith('Global status changed to')) {
                        const match = part.match(/Global status changed to (.*)/);
                        const newStatus = match ? match[1] : '';
                        const oldStatus = newStatus === 'true' ? 'false' : 'true';
                        lines.push({ type: 'delete', text: `- Global: ${oldStatus}` });
                        lines.push({ type: 'add', text: `+ Global: ${newStatus}` });
                    } else if (part.startsWith('Added values:')) {
                        const vals = part.replace('Added values: [', '').replace(']', '');
                        vals.split(', ').forEach(v => {
                            lines.push({ type: 'add', text: `+ Value: ${v}` });
                        });
                    } else if (part.startsWith('Removed values:')) {
                        const vals = part.replace('Removed values: [', '').replace(']', '');
                        vals.split(', ').forEach(v => {
                            lines.push({ type: 'delete', text: `- Value: ${v}` });
                        });
                    } else {
                        lines.push({ type: 'neutral', text: part });
                    }
                });
            }
        }
    }

    const getDiffCategory = (text) => {
        if (!text) return '';
        let clean = text;
        if (text.startsWith('+ ') || text.startsWith('- ')) {
            clean = text.substring(2);
        }
        const colonIdx = clean.indexOf(':');
        if (colonIdx !== -1) {
            return clean.substring(0, colonIdx).trim();
        }
        return clean.trim();
    };

    const pairs = [];
    const usedIndices = new Set();

    for (let i = 0; i < lines.length; i++) {
        if (usedIndices.has(i)) continue;
        const current = lines[i];

        if (current.type === 'neutral') {
            pairs.push({ left: current, right: current });
            usedIndices.add(i);
        } else if (current.type === 'delete') {
            const currentCat = getDiffCategory(current.text);
            let foundMatchIdx = -1;
            for (let k = i + 1; k < lines.length; k++) {
                if (!usedIndices.has(k) && lines[k].type === 'add') {
                    if (getDiffCategory(lines[k].text) === currentCat) {
                        foundMatchIdx = k;
                        break;
                    }
                }
            }
            if (foundMatchIdx !== -1) {
                pairs.push({ left: current, right: lines[foundMatchIdx] });
                usedIndices.add(i);
                usedIndices.add(foundMatchIdx);
            } else {
                pairs.push({ left: current, right: null });
                usedIndices.add(i);
            }
        } else if (current.type === 'add') {
            const currentCat = getDiffCategory(current.text);
            let foundMatchIdx = -1;
            for (let k = i + 1; k < lines.length; k++) {
                if (!usedIndices.has(k) && lines[k].type === 'delete') {
                    if (getDiffCategory(lines[k].text) === currentCat) {
                        foundMatchIdx = k;
                        break;
                    }
                }
            }
            if (foundMatchIdx !== -1) {
                pairs.push({ left: lines[foundMatchIdx], right: current });
                usedIndices.add(i);
                usedIndices.add(foundMatchIdx);
            } else {
                pairs.push({ left: null, right: current });
                usedIndices.add(i);
            }
        }
    }

    const getDiffSimilarityScore = (rowA, rowB) => {
        let score = 0;
        const keys = Object.keys(rowA);
        keys.forEach(k => {
            if (rowA[k] !== undefined && rowA[k] === rowB[k]) {
                score++;
            }
        });
        return score;
    };

    const matchedAddedIndices = new Set();
    const deleteToAddMatch = {};

    for (let dIdx = 0; dIdx < deletedRows.length; dIdx++) {
        let bestScore = -1;
        let bestAIdx = -1;
        for (let aIdx = 0; aIdx < addedRows.length; aIdx++) {
            if (matchedAddedIndices.has(aIdx)) continue;
            const score = getDiffSimilarityScore(deletedRows[dIdx], addedRows[aIdx]);
            if (score > bestScore) {
                bestScore = score;
                bestAIdx = aIdx;
            }
        }
        if (bestAIdx !== -1 && (bestScore > 0 || (deletedRows.length === 1 && addedRows.length === 1))) {
            deleteToAddMatch[dIdx] = bestAIdx;
            matchedAddedIndices.add(bestAIdx);
        }
    }

    const pairedTableRows = [];
    for (let dIdx = 0; dIdx < deletedRows.length; dIdx++) {
        if (dIdx in deleteToAddMatch) {
            pairedTableRows.push({
                left: deletedRows[dIdx],
                right: addedRows[deleteToAddMatch[dIdx]]
            });
        } else {
            pairedTableRows.push({
                left: deletedRows[dIdx],
                right: null
            });
        }
    }

    for (let aIdx = 0; aIdx < addedRows.length; aIdx++) {
        if (!matchedAddedIndices.has(aIdx)) {
            pairedTableRows.push({
                left: null,
                right: addedRows[aIdx]
            });
        }
    }

    return (
        <div style={{ marginBottom: '1rem' }}>
            <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '0.5rem',
                border: '1px solid var(--border)',
                borderRadius: '6px',
                overflow: 'hidden',
                background: 'var(--bg-primary)'
            }}>
                {/* Headers */}
                <div style={{
                    background: 'var(--bg-secondary)',
                    padding: '0.4rem 0.6rem',
                    borderBottom: '1px solid var(--border)',
                    fontWeight: 'bold',
                    fontSize: '0.75rem',
                    color: 'var(--text-secondary)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                }}>
                    Before (Old State)
                </div>
                <div style={{
                    background: 'var(--bg-secondary)',
                    padding: '0.4rem 0.6rem',
                    borderBottom: '1px solid var(--border)',
                    borderLeft: '1px solid var(--border)',
                    fontWeight: 'bold',
                    fontSize: '0.75rem',
                    color: 'var(--text-secondary)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                }}>
                    After (New State)
                </div>

                {/* Rows */}
                {pairs.map((pair, idx) => {
                    let leftBg = 'transparent';
                    let leftColor = 'var(--text-secondary)';
                    let leftDecoration = 'none';
                    if (pair.left?.type === 'delete') {
                        leftBg = 'rgba(220, 38, 38, 0.08)';
                        leftColor = 'var(--error)';
                        leftDecoration = 'line-through';
                    }

                    let rightBg = 'transparent';
                    let rightColor = 'var(--text-secondary)';
                    if (pair.right?.type === 'add') {
                        rightBg = 'rgba(5, 150, 105, 0.08)';
                        rightColor = 'var(--success)';
                    }

                    return (
                        <Fragment key={idx}>
                            <div style={{
                                padding: '0.35rem 0.5rem',
                                fontSize: '0.8rem',
                                fontFamily: 'monospace',
                                background: leftBg,
                                color: leftColor,
                                textDecoration: leftDecoration,
                                minHeight: '1.5rem',
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-all'
                            }}>
                                {pair.left ? pair.left.text : ''}
                            </div>
                            <div style={{
                                padding: '0.35rem 0.5rem',
                                fontSize: '0.8rem',
                                fontFamily: 'monospace',
                                background: rightBg,
                                color: rightColor,
                                borderLeft: '1px solid var(--border)',
                                minHeight: '1.5rem',
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-all'
                            }}>
                                {pair.right ? pair.right.text : ''}
                            </div>
                        </Fragment>
                    );
                })}
            </div>
            
            {hasTableDiff && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginTop: '1rem' }}>
                    {/* Left Table: DELETED */}
                    <div style={{ overflowX: 'auto', border: '1px solid var(--border)', borderRadius: '6px', background: 'var(--bg-primary)' }}>
                        <div style={{
                            background: 'var(--bg-secondary)',
                            padding: '0.4rem 0.6rem',
                            borderBottom: '1px solid var(--border)',
                            fontSize: '0.75rem',
                            fontWeight: 'bold',
                            color: 'var(--text-secondary)',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em'
                        }}>
                            Deleted Rows
                        </div>
                        <table style={{ width: '100%', tableLayout: 'fixed', borderCollapse: 'collapse', fontSize: '0.8rem', textAlign: 'left' }}>
                            <thead>
                                <tr style={{ background: 'var(--bg-secondary)', borderBottom: '1px solid var(--border)' }}>
                                    {columns.map(col => (
                                        <th key={col} style={{
                                            padding: '0.4rem 0.6rem',
                                            fontWeight: 'bold',
                                            color: 'var(--text-primary)',
                                            borderRight: '1px solid var(--border)',
                                            width: `${100 / columns.length}%`,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap'
                                        }}>
                                            {col}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {pairedTableRows.map((pair, rIdx) => (
                                    <tr key={rIdx} style={{
                                        background: pair.left ? 'rgba(220, 38, 38, 0.04)' : 'transparent',
                                        color: 'var(--error)',
                                        borderBottom: '1px solid var(--border)',
                                        textDecoration: pair.left ? 'line-through' : 'none',
                                        height: '2.2rem'
                                    }}>
                                        {columns.map(col => (
                                            <td key={col} style={{
                                                padding: '0.4rem 0.6rem',
                                                borderRight: '1px solid var(--border)',
                                                fontFamily: 'monospace',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap'
                                            }}>
                                                {pair.left ? (pair.left[col] || '') : ''}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Right Table: ADDED */}
                    <div style={{ overflowX: 'auto', border: '1px solid var(--border)', borderRadius: '6px', background: 'var(--bg-primary)' }}>
                        <div style={{
                            background: 'var(--bg-secondary)',
                            padding: '0.4rem 0.6rem',
                            borderBottom: '1px solid var(--border)',
                            fontSize: '0.75rem',
                            fontWeight: 'bold',
                            color: 'var(--text-secondary)',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em'
                        }}>
                            Added Rows
                        </div>
                        <table style={{ width: '100%', tableLayout: 'fixed', borderCollapse: 'collapse', fontSize: '0.8rem', textAlign: 'left' }}>
                            <thead>
                                <tr style={{ background: 'var(--bg-secondary)', borderBottom: '1px solid var(--border)' }}>
                                    {columns.map(col => (
                                        <th key={col} style={{
                                            padding: '0.4rem 0.6rem',
                                            fontWeight: 'bold',
                                            color: 'var(--text-primary)',
                                            borderRight: '1px solid var(--border)',
                                            width: `${100 / columns.length}%`,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap'
                                        }}>
                                            {col}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {pairedTableRows.map((pair, rIdx) => (
                                    <tr key={rIdx} style={{
                                        background: pair.right ? 'rgba(5, 150, 105, 0.04)' : 'transparent',
                                        color: 'var(--success)',
                                        borderBottom: '1px solid var(--border)',
                                        height: '2.2rem'
                                    }}>
                                        {columns.map(col => (
                                            <td key={col} style={{
                                                padding: '0.4rem 0.6rem',
                                                borderRight: '1px solid var(--border)',
                                                fontFamily: 'monospace',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap'
                                            }}>
                                                {pair.right ? (pair.right[col] || '') : ''}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DiffView;
